import React, { useState } from 'react';
import './footer.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-content">
          {children}
        </div>
      </div>
    </div>
  );
}

const Footer: React.FC = () => {
  const [modalType, setModalType] = useState<string | null>(null);

  const openModal = (type: string) => setModalType(type);
  const closeModal = () => setModalType(null);

  const technologyContent = (
    <div className="content-section">
      <h3>Core Demo Technologies</h3>
      
      <div className="demo-feature">
        <h4>1. AI-Powered Chatbot System</h4>
        <ul>
          <li><strong>24/7 Academic Support</strong> - Instant answers to subject queries</li>
          <li><strong>Homework Helper</strong> - Step-by-step problem solving and explanations</li>
          <li><strong>Automated Assessment</strong> - Instant grading with detailed feedback</li>
          <li><strong>Personalized Learning</strong> - Adaptive content based on student progress</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>2. Intelligent Voice Assistant</h4>
        <ul>
          <li><strong>Voice-Activated Learning</strong> - Hands-free access to study materials</li>
          <li><strong>Language Learning Aid</strong> - Pronunciation correction and practice</li>
          <li><strong>Accessibility Features</strong> - Support for differently-abled students</li>
          <li><strong>Voice Commands</strong> - Control school systems and access information</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>3. Smart Assessment Platform</h4>
        <ul>
          <li><strong>Automated Test Generation</strong>: Create customized unit tests and quizzes</li>
          <li><strong>Real-time Analytics</strong>: Track student performance metrics</li>
          <li><strong>Adaptive Testing</strong>: Questions adjust to student ability level</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>4. Parent & Teacher Portal</h4>
        <ul>
          <li><strong>Progress Tracking</strong>: Real-time academic monitoring</li>
          <li><strong>Automated Reporting</strong>: Instant grade books and analytics</li>
          <li><strong>Communication Hub</strong>: Direct messaging and announcements</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>4. Smart Campus Features</h4>
        <ul>
          <li><strong>Automated Lighting & Climate Control</strong> - Motion-sensor activated systems</li>
          <li><strong>Smart Waste Management</strong> - Automated waste sorting and collection</li>
          <li><strong>Digital Signage</strong> - Interactive displays for announcements and wayfinding</li>
        </ul>
      </div>
    </div>
  );

  const sopContent = (
    <div className="content-section">
      <h3>How It Works</h3>
      
      <div className="demo-feature">
        <h4>AI-Powered Learning System</h4>
        <ul>
          <li><strong>Natural Language Processing</strong> - Understands student queries in natural language</li>
          <li><strong>Context-Aware Responses</strong> - Maintains conversation context</li>
          <li><strong>Learning Analytics</strong> - Tracks student progress and adapts to learning patterns</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>Voice Assistant Integration</h4>
        <ul>
          <li><strong>Voice Recognition</strong> - Accurately captures and processes voice commands</li>
          <li><strong>Multi-language Support</strong> - Works across different languages</li>
          <li><strong>Accessibility Features</strong> - Voice feedback and screen reader compatibility</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>Automated Assessment System</h4>
        <ul>
          <li><strong>Homework Submission</strong> - Digital submission and tracking</li>
          <li><strong>AI Grading</strong> - Instant feedback and scoring</li>
          <li><strong>Progress Reports</strong> - Automated generation and distribution</li>
        </ul>
      </div>

      <div className="demo-feature">
        <h4>Parent-Teacher Communication</h4>
        <ul>
          <li><strong>Real-time Updates</strong> - Instant notifications about student progress</li>
          <li><strong>Meeting Scheduler</strong> - Automated calendar integration</li>
          <li><strong>Performance Analytics</strong> - Detailed insights and recommendations</li>
        </ul>
      </div>
    </div>
  );

  const useCaseContent = (
    <div className="content-section">
      <h3>Key Features & Future Vision</h3>
      
      <div className="use-case">
        <h4>1. Smart Homework & Assessment System</h4>
        <ul>
          <li><strong>Automated Homework Assignment</strong>: Digital distribution and submission</li>
          <li><strong>AI-Powered Grading</strong>: Instant feedback on assignments and tests</li>
          <li><strong>Plagiarism Detection</strong>: Ensures academic integrity</li>
          <li><strong>Unit Test Generator</strong>: Creates customized practice tests</li>
        </ul>
      </div>

      <div className="use-case">
        <h4>2. Voice-Enabled Learning</h4>
        <ul>
          <li><strong>Voice-Activated Quizzes</strong>: Interactive oral assessments</li>
          <li><strong>Language Lab Integration</strong>: Perfect pronunciation practice</li>
          <li><strong>Voice Notes</strong>: Record and transcribe lectures automatically</li>
          <li><strong>Reduced Teacher Workload</strong>: Handles routine queries, allowing teachers to focus on complex topics</li>
          <li><strong>Personalized Learning Paths</strong>: Adapts to individual student needs and learning styles</li>
        </ul>
      </div>

      <div className="use-case">
        <h4>2. Voice Assistant Applications</h4>
        <ul>
          <li><strong>Hands-Free Operation</strong>: Control systems and access information without physical interaction</li>
          <li><strong>Accessibility</strong>: Makes technology accessible to visually impaired students</li>
          <li><strong>Language Learning</strong>: Helps with pronunciation and language practice</li>
        </ul>
      </div>

      <div className="use-case">
        <h4>3. Sustainable Campus Features</h4>
        <ul>
          <li><strong>Rainwater Harvesting</strong>: Reduces water consumption by 40%</li>
          <li><strong>Solar Energy</strong>: Cuts electricity costs by up to 60%</li>
          <li><strong>Smart Lighting</strong>: Motion-activated systems reduce energy waste</li>
        </ul>
      </div>

      <div className="use-case">
        <h4>4. Robotics & Automation</h4>
        <ul>
          <li><strong>Educational Robots</strong>: Teach programming and robotics concepts</li>
          <li><strong>Automated Maintenance</strong>: Keeps the campus clean and well-maintained</li>
          <li><strong>Enhanced Security</strong>: 24/7 surveillance and monitoring</li>
        </ul>
      </div>

      <div className="advantages">
        <h4>Measurable Impact:</h4>
        <ul>
          <li>40% reduction in operational costs</li>
          <li>35% improvement in student engagement</li>
          <li>90% reduction in paper usage</li>
          <li>60% energy savings through smart systems</li>
        </ul>
      </div>
    </div>
  );

  return (
    <footer className="footer">
      <div className="footer-content">
        <h2 className="footer-title"><span className="highlight">Bishop Corrie Future Tech Prototype (FTP)</span></h2>
        <p className="footer-subtitle">
          Pioneering the Future of Education: Smart Technologies for Enhanced Learning Environments and Infrastructure Development
        </p>
        <div className="footer-buttons">
          <button 
            className="footer-button" 
            onClick={() => openModal('technology')}
          >
            Smart Innovations
          </button>
          <button 
            className="footer-button" 
            onClick={() => openModal('sop')}
          >
            How It Works
          </button>
          <button 
            className="footer-button" 
            onClick={() => openModal('usecase')}
          >
            Use Cases & Advantages
          </button>
        </div>
      </div>

       {/* Add this new div for developer credits */}
  <div style={{ 
    marginTop: '20px', 
    textAlign: 'center', 
    fontSize: '0.9rem',
    color: '#eee',
    padding: '10px 0',
    borderTop: '1px solid #eee'
  }}>
    Developed by Ahmad Taufeeq Hussain, Kathiresan, Dilipan
  </div>
  <div style={{ 
    
    textAlign: 'center', 
    fontSize: '0.9rem',
    color: '#eee',
    padding: '10px 0',
    
  }}>
    Ideas Presented by Class 11-B
  </div>

      <Modal isOpen={modalType === 'technology'} onClose={closeModal} title="Next-Gen Learning Capabilities">
        {technologyContent}
      </Modal>

      <Modal isOpen={modalType === 'sop'} onClose={closeModal} title="How It Works">
        {sopContent}
      </Modal>

      <Modal isOpen={modalType === 'usecase'} onClose={closeModal} title="Use Cases & Advantages">
        {useCaseContent}
      </Modal>
    </footer>
  );
};

export default Footer;